// 보행 격자 + A* 길찾기. 프로토타입 850–925.
// three.js를 쓰지 않는 순수 TS: 나중에 packages/shared로 옮겨 서버에서도 같은 길찾기를 쓴다.

export interface NavConfig {
  /** 격자 왼쪽 아래 모서리 (월드 x, z). */
  x0: number;
  z0: number;
  /** 칸 크기 (m). */
  cs: number;
  nx: number;
  nz: number;
}

export interface NavPoint { x: number; z: number }

/** 지나갈 점 목록. 목표에 닿지 못하면 partial = true (가장 가까운 곳까지). */
export type NavPath = NavPoint[] & { partial?: boolean };

export interface NavGrid {
  readonly cfg: NavConfig;
  /** 1 = 막힘, 0 = 열림. 인덱스는 j * nx + i. */
  readonly cells: Uint8Array;
  cellI(x: number): number;
  cellJ(z: number): number;
  openCell(i: number, j: number): boolean;
  blockedAt(x: number, z: number): boolean;
  /** 칸 중심이 test를 만족하는 칸을 모두 막는다 (경계, 물, 사각형 장애물 등). */
  blockWhere(test: (x: number, z: number) => boolean): void;
  /** 원 안의 칸을 막는다. */
  markCircle(x: number, z: number, r: number): void;
  /** (i, j)가 막혀 있으면 가장 가까운 열린 칸 (반경 15칸까지). */
  nearestOpen(i: number, j: number): [number, number] | null;
  /** 두 점 사이를 0.2m 간격으로 확인해 막힌 곳이 없으면 true. */
  los(ax: number, az: number, bx: number, bz: number): boolean;
  findPath(fx: number, fz: number, gx: number, gz: number): NavPath;
}

/** 한 번 길찾기에서 펼칠 칸 수 상한. */
const MAX_ITER = 120000;

export function createNavGrid(cfg: NavConfig): NavGrid {
  const { x0, z0, cs, nx, nz } = cfg;
  const N = nx * nz;
  const cells = new Uint8Array(N);
  // 탐색 버퍼는 격자마다 하나. stamp/closed에 탐색 번호를 적어 매번 지우지 않는다.
  const gS = new Float32Array(N), fS = new Float32Array(N), came = new Int32Array(N), stamp = new Uint32Array(N), closed = new Uint32Array(N);
  let searchId = 0;

  const cellI = (x: number) => Math.floor((x - x0) / cs);
  const cellJ = (z: number) => Math.floor((z - z0) / cs);
  const openCell = (i: number, j: number) => i >= 0 && j >= 0 && i < nx && j < nz && !cells[j * nx + i];
  const blockedAt = (x: number, z: number) => !openCell(cellI(x), cellJ(z));

  function blockWhere(test: (x: number, z: number) => boolean) {
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      if (test(x0 + (i + 0.5) * cs, z0 + (j + 0.5) * cs)) cells[j * nx + i] = 1;
    }
  }

  function markCircle(x: number, z: number, r: number) {
    const i0 = Math.floor((x - r - x0) / cs), i1 = Math.floor((x + r - x0) / cs), j0 = Math.floor((z - r - z0) / cs), j1 = Math.floor((z + r - z0) / cs);
    for (let j = Math.max(0, j0); j <= Math.min(nz - 1, j1); j++) for (let i = Math.max(0, i0); i <= Math.min(nx - 1, i1); i++) {
      const cx = x0 + (i + 0.5) * cs, cz = z0 + (j + 0.5) * cs;
      if (Math.hypot(cx - x, cz - z) < r) cells[j * nx + i] = 1;
    }
  }

  function nearestOpen(i: number, j: number): [number, number] | null {
    if (openCell(i, j)) return [i, j];
    for (let r = 1; r < 16; r++) for (let dj = -r; dj <= r; dj++) for (let di = -r; di <= r; di++) {
      if (Math.max(Math.abs(di), Math.abs(dj)) !== r) continue;
      if (openCell(i + di, j + dj)) return [i + di, j + dj];
    }
    return null;
  }

  function los(ax: number, az: number, bx: number, bz: number) {
    const d = Math.hypot(bx - ax, bz - az), n = Math.ceil(d / 0.2);
    for (let k = 1; k < n; k++) {
      const u = k / n;
      if (blockedAt(ax + (bx - ax) * u, az + (bz - az) * u)) return false;
    }
    return true;
  }

  function findPath(fx: number, fz: number, gx: number, gz: number): NavPath {
    const s = nearestOpen(cellI(fx), cellJ(fz)), g = nearestOpen(cellI(gx), cellJ(gz));
    if (!s || !g) return [{ x: gx, z: gz }];
    searchId++;
    const si = s[1] * nx + s[0], gi = g[1] * nx + g[0];
    const heap = [si];
    stamp[si] = searchId; gS[si] = 0; came[si] = -1;
    // 8방향 격자의 옥타일 거리
    const h = (k: number) => {
      const dx = Math.abs(k % nx - g[0]), dz = Math.abs(((k / nx) | 0) - g[1]);
      return Math.max(dx, dz) + 0.414 * Math.min(dx, dz);
    };
    fS[si] = h(si);
    const push = (k: number) => {
      heap.push(k);
      let c = heap.length - 1;
      while (c > 0) {
        const p = (c - 1) >> 1;
        if (fS[heap[p]!]! <= fS[heap[c]!]!) break;
        [heap[p], heap[c]] = [heap[c]!, heap[p]!];
        c = p;
      }
    };
    const pop = () => {
      const top = heap[0]!, last = heap.pop()!;
      if (heap.length) {
        heap[0] = last;
        let c = 0;
        for (;;) {
          const l = c * 2 + 1, r = l + 1;
          let m = c;
          if (l < heap.length && fS[heap[l]!]! < fS[heap[m]!]!) m = l;
          if (r < heap.length && fS[heap[r]!]! < fS[heap[m]!]!) m = r;
          if (m === c) break;
          [heap[m], heap[c]] = [heap[c]!, heap[m]!];
          c = m;
        }
      }
      return top;
    };

    let found = false, iter = 0, best = si, bestH = 1e9;
    while (heap.length && iter++ < MAX_ITER) {
      const cur = pop();
      if (closed[cur] === searchId) continue;
      closed[cur] = searchId;
      if (cur === gi) { found = true; break; }
      const hc = h(cur);
      if (hc < bestH) { bestH = hc; best = cur; }
      const ci = cur % nx, cj = (cur / nx) | 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        if (!di && !dj) continue;
        const ni = ci + di, nj = cj + dj;
        if (!openCell(ni, nj)) continue;
        // 대각선은 양옆 두 칸이 모두 열려 있어야 (모서리 끼어 지나가기 방지)
        if (di && dj && (!openCell(ci + di, cj) || !openCell(ci, cj + dj))) continue;
        const nk = nj * nx + ni, cost = gS[cur]! + (di && dj ? 1.414 : 1);
        if (closed[nk] === searchId) continue;
        if (stamp[nk] !== searchId || cost < gS[nk]!) { stamp[nk] = searchId; gS[nk] = cost; came[nk] = cur; fS[nk] = cost + h(nk); push(nk); }
      }
    }

    const endK = found ? gi : best;
    const cellsOnPath: number[] = [];
    for (let k = endK; k !== -1; k = came[k]!) cellsOnPath.push(k);
    cellsOnPath.reverse();
    const pts = cellsOnPath.map((k) => ({ x: x0 + (k % nx + 0.5) * cs, z: z0 + (((k / nx) | 0) + 0.5) * cs }));
    // 직선으로 보이는 가장 먼 점까지 건너뛰어 꺾임을 줄인다
    const out: NavPath = [];
    let cx = pts[0]!.x, cz = pts[0]!.z, idx = 1;
    out.push(pts[0]!);
    while (idx < pts.length) {
      let far = idx;
      for (let k = pts.length - 1; k > idx; k--) { if (los(cx, cz, pts[k]!.x, pts[k]!.z)) { far = k; break; } }
      out.push(pts[far]!);
      cx = pts[far]!.x; cz = pts[far]!.z; idx = far + 1;
    }
    if (found) out.push({ x: gx, z: gz });
    else out.partial = true;
    return out;
  }

  return { cfg, cells, cellI, cellJ, openCell, blockedAt, blockWhere, markCircle, nearestOpen, los, findPath };
}
